import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { lt } from '@mews/framework';
import { SegmentedControl, SegmentedControlItem, Stack, Typography } from '@optimus-web/core';
import { createOption } from '@mews-commander/ui';
import { AdjustmentType, getCurrentServiceFieldName } from '../../../forms/schemas/configureProductFormSchema';
import { useEnterprisesProductContext } from '../../../../context/EnterprisesProductContext';
import { useEnterpriseRelationships } from '../../../../queries/ProductForm';
import { FixedPricing } from './FixedPricing/FixedPricing';
import { PercentagePricing } from './PercentagePricing/PercentagePricing';

interface Props {
    currentService: string;
}
export const ServicesPricingMultipleProperties = ({ currentService }: Props) => {
    const {
        state: { selectedEnterpriseIds },
    } = useEnterprisesProductContext();

    const { watch } = useFormContext();

    const currentServiceFieldName = getCurrentServiceFieldName(currentService);
    const currentProperties = watch(currentServiceFieldName);

    const { data } = useEnterpriseRelationships(selectedEnterpriseIds);

    const [activeAdjustmentSegment, setActiveAdjustmentSegment] = useState<AdjustmentType>(
        AdjustmentType.FixedPriceAdjustments
    );

    const propertiesOptions =
        data?.data.map(({ id, name }) =>
            createOption({
                label: name,
                value: id,
                // check if selected property is in opposite adjustment to make it disabled
                disabled: Object.keys(
                    currentProperties[
                        activeAdjustmentSegment === AdjustmentType.FixedPriceAdjustments
                            ? AdjustmentType.PercentagePriceAdjustments
                            : AdjustmentType.FixedPriceAdjustments
                    ] || ''
                ).includes(id),
            })
        ) || [];

    return (
        <>
            <Stack vertical spacing="200">
                <Typography textStyle="bodyLargeStrong" as="div">
                    {lt('ConfigureProductServicesSubHeadingPrice')}
                </Typography>
                <Typography textStyle="bodyMedium" variant="secondary" as="div">
                    {lt('ConfigureProductServicesAdjustmentBody')}
                </Typography>
            </Stack>
            <SegmentedControl
                value={activeAdjustmentSegment}
                onChange={(e, tab) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setActiveAdjustmentSegment(tab as AdjustmentType);
                }}
            >
                <SegmentedControlItem label={lt('GeneralFixedPrice')} value={AdjustmentType.FixedPriceAdjustments} />
                <SegmentedControlItem
                    label={lt('GeneralPercentage')}
                    value={AdjustmentType.PercentagePriceAdjustments}
                />
            </SegmentedControl>
            {activeAdjustmentSegment === AdjustmentType.FixedPriceAdjustments ? (
                <FixedPricing list={propertiesOptions} currentService={currentService} />
            ) : (
                <PercentagePricing list={propertiesOptions} currentService={currentService} />
            )}
        </>
    );
};
